import {
  Button,
  ButtonGroup,
  Dialog,
  DialogFooter,
  EditableText,
  MenuItem,
} from '@blueprintjs/core';
import classNames from 'classnames';
import React, { useState } from 'react';

import { Select2 } from '@blueprintjs/select';
import { getNames } from 'country-list';
import { useAppDispatch } from 'hooks';
import { createClub } from 'store/registration';
import { ClimberCategory } from '../../constants';
import styles from './styles.module.scss';

export default function ClimberRow({
  climberData,
  editDisabled,
  onClimberChange,
  climberChanged,
  usedNumbers,
  originalNumber,
  clubs,
  onSave,
  onDiscard,
}: {
  climberData: RegisteredClimber;
  editDisabled: boolean;
  onClimberChange: (climber: RegisteredClimber) => void;
  climberChanged: boolean;
  usedNumbers: number[];
  originalNumber: number;
  clubs: string[];
  onSave: (data: RegisteredClimber) => Promise<void>;
  onDiscard: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);

  const dispatch = useAppDispatch();

  const nubmerInvalid =
    climberData.number === 0 ||
    (climberChanged &&
      climberData.number !== originalNumber &&
      usedNumbers.includes(climberData.number));

  const nameInvalid = climberData.name === '';
  const categoryInvalid = !Object.values(ClimberCategory).includes(
    climberData.category as ClimberCategory
  );
  const clubInvalid = false;

  return (
    <React.Fragment>
      <EditableText
        disabled={editDisabled}
        value={String(climberData.number)}
        type="numeric"
        onChange={(value) => {
          const number = Number(value);
          if (isNaN(number)) return;

          onClimberChange({
            ...climberData,
            number,
          });
        }}
        className={classNames(
          nubmerInvalid && styles.invalid,
          editDisabled && styles.disabled,
          climberChanged && styles.changed
        )}
      />

      <EditableText
        value={String(climberData.name)}
        disabled={editDisabled}
        placeholder="Name"
        onChange={(value) =>
          onClimberChange({
            ...climberData,
            name: value,
          })
        }
        className={classNames(
          nameInvalid && styles.invalid,
          editDisabled && styles.disabled,
          climberChanged && styles.changed
        )}
      />

      <Select2
        className={classNames(
          categoryInvalid && styles.invalid,
          editDisabled && styles.disabled,
          climberChanged && styles.changed,
          styles.selectable
        )}
        disabled={editDisabled}
        items={Object.values(ClimberCategory)}
        itemPredicate={(query, category) =>
          category.toUpperCase().includes(query.toUpperCase())
        }
        itemRenderer={(category) => (
          <MenuItem
            key={category}
            text={category}
            onClick={() =>
              onClimberChange({
                ...climberData,
                category,
              })
            }
          />
        )}
        onItemSelect={() => {}}
      >
        {climberData.category}
      </Select2>
      <Select2
        className={classNames(
          clubInvalid && styles.invalid,
          editDisabled && styles.disabled,
          climberChanged && styles.changed,
          styles.selectable
        )}
        createNewItemFromQuery={(clubName) => clubName}
        createNewItemRenderer={(query) => {
          if (
            query.length < 2 ||
            clubs.some((club) => club.toLowerCase() === query.toLowerCase())
          ) {
            return undefined;
          }
          return (
            <MenuItem
              text={`Add new club "${query}"`}
              onClick={async () => {
                const newClub = await dispatch(createClub(query));
                if (newClub) {
                  onClimberChange({
                    ...climberData,
                    club: newClub,
                  });
                }
              }}
            />
          );
        }}
        disabled={editDisabled}
        items={clubs}
        itemRenderer={(club) => (
          <MenuItem
            key={club}
            text={club}
            onClick={() => onClimberChange({ ...climberData, club })}
          />
        )}
        onItemSelect={() => {}}
      >
        {climberData.club || '–'}
      </Select2>

      <Select2
        className={classNames(
          clubInvalid && styles.invalid,
          editDisabled && styles.disabled,
          climberChanged && styles.changed,
          styles.selectable
        )}
        disabled={editDisabled}
        items={getNames()}
        itemRenderer={(country) => (
          <MenuItem
            key={country}
            text={country}
            onClick={() => {
              onClimberChange({
                ...climberData,
                country,
              });
            }}
          />
        )}
        itemListPredicate={(query, values) => {
          return values.filter((value) =>
            value.toLowerCase().startsWith(query.toLowerCase())
          );
        }}
        onItemSelect={() => {}}
      >
        {climberData.country || '–'}
      </Select2>

      <EditableText
        value={String(climberData.paid || '')}
        placeholder="-"
        disabled={editDisabled}
        type="numeric"
        onChange={(value) => {
          const paid = Number(value);
          if (isNaN(paid)) return;

          onClimberChange({
            ...climberData,
            paid,
          });
        }}
        className={classNames(
          editDisabled && styles.disabled,
          climberChanged && styles.changed
        )}
      />

      <input
        type="text"
        value={climberData.notes}
        onChange={() => {}}
        placeholder="-"
        onClick={() => setNotesOpen(true)}
        className={classNames(
          editDisabled && styles.disabled,
          climberChanged && styles.changed,
          styles.notesInput
        )}
      />

      <Dialog
        isOpen={notesOpen}
        title={climberData.name + ' - notes'}
        onClose={() => setNotesOpen(false)}
        isCloseButtonShown={false}
      >
        <textarea
          autoFocus
          rows={10}
          className={styles.notesTextArea}
          value={climberData.notes}
          onChange={(e) =>
            onClimberChange({ ...climberData, notes: e.target.value })
          }
          onFocus={(e) => (e.target.selectionStart = e.target.value.length)}
          placeholder="Add note.."
        />
        <DialogFooter
          actions={
            <Button
              intent="success"
              text="Ok"
              onClick={() => setNotesOpen(false)}
            />
          }
        />
      </Dialog>

      {climberChanged ? (
        <ButtonGroup className={styles.buttons}>
          <Button loading={loading} text={'Discard'} onClick={onDiscard} />
          <Button
            disabled={
              nubmerInvalid || nameInvalid || categoryInvalid || clubInvalid
            }
            loading={loading}
            intent="success"
            text={'Save'}
            onClick={async () => {
              setLoading(true);
              await onSave(climberData);
              setLoading(false);
            }}
          />
        </ButtonGroup>
      ) : (
        <div />
      )}
    </React.Fragment>
  );
}
